import React from "react";
import { Icon } from "../UIElements/Icon";
import { Switch } from "../UIElements/Switch";
import { useToast } from "@finitless/shared/hooks";
import { PROMO_STATUS } from "./discountsConstants";

const PromotionCard = ({
  promotion,
  onEdit,
  onDelete,
  onToggleStatus,
  isUpdating,
}) => {
  const toast = useToast();
  if (!promotion) {
    return null;
  }

  const isActive = promotion.status === PROMO_STATUS.ACTIVE;
  const isExpired = promotion.status === PROMO_STATUS.EXPIRED;
  const isDisabled = promotion.status === PROMO_STATUS.DISABLED;
  const apiEnabled = promotion.raw?.enabled;
  const hasCode = Boolean(promotion.coupon && String(promotion.coupon).trim());
  const displayCode = hasCode ? promotion.coupon : "No code";
  const getStatusBadgeClasses = () => {
    if (isActive)
      return "bg-[var(--success-bg)] text-success border-success/30";
    if (isExpired)
      return "bg-[var(--error-bg)] text-destructive border-destructive/30";
    if (isDisabled) return "bg-card/60 text-muted-foreground border-border";
    return "bg-accent/10 text-primary border-primary/30";
  };

  const handleCardClick = () => {
    if (isUpdating) {
      return;
    }
    onEdit(promotion);
  };

  const handleActionClick = (event, action) => {
    event.stopPropagation();
    if (isUpdating) {
      return;
    }
    action();
  };

  return (
    <div
      onClick={handleCardClick}
      tabIndex={0}
      role="button"
      aria-label={`Open promotion ${promotion.name}`}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      }}
      className={`ordering-discounts-card border-border bg-background hover:border-primary/40 focus:ring-ring relative cursor-pointer overflow-hidden rounded-xl border transition-all duration-300 select-none hover:shadow-xl focus:ring-2 focus:outline-none ${
        isUpdating ? "pointer-events-none cursor-wait opacity-60" : ""
      }`}
    >
      <div className="relative h-40 overflow-hidden">
        {promotion.image ? (
          <img
            src={promotion.image}
            alt={promotion.name}
            className={`h-full w-full object-cover transition-transform duration-500 ${
              isDisabled ? "grayscale" : "hover:scale-105"
            }`}
          />
        ) : (
          <div className="bg-card text-muted-foreground flex h-full w-full items-center justify-center text-sm">
            No image
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold backdrop-blur-md ${getStatusBadgeClasses()}`}
          >
            {promotion.status}
          </span>
        </div>
        <div className="bg-background/70 absolute right-0 bottom-0 left-0 h-20" />
      </div>

      <div className="p-5">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3
              className={`mb-1 line-clamp-2 min-h-[2.75rem] text-lg leading-tight font-semibold break-words ${
                isDisabled ? "text-muted-foreground" : "text-white"
              }`}
            >
              {promotion.name}
            </h3>
            <p className="text-muted-foreground flex items-center gap-1 text-xs">
              <Icon name="Calendar" className="h-3 w-3" />
              {promotion.start} - {promotion.end}
            </p>
          </div>
        </div>

        <div className="mt-1 h-5">
          <p className="text-muted-foreground truncate text-sm">
            {promotion.description}
          </p>
        </div>

        <div className="border-border bg-background/60 mt-4 flex items-center justify-between rounded-lg border p-3">
          <div className="text-left">
            <p className="text-muted-foreground text-xs font-medium uppercase">
              {promotion.raw?.type === 2 ? "Code" : "Automatic"}
            </p>
            {promotion.raw?.type === 2 && (
              <>
                <div className="flex items-center gap-2">
                  <code
                    className={`font-mono font-semibold ${
                      !hasCode
                        ? "text-muted-foreground"
                        : isDisabled
                          ? "text-muted-foreground"
                          : "text-primary"
                    }`}
                  >
                    {displayCode}
                  </code>
                  {hasCode && (
                    <button
                      type="button"
                      onClick={(event) =>
                        handleActionClick(event, () =>
                          navigator.clipboard
                            .writeText(promotion.coupon || "")
                            .then(() =>
                              toast.success("Coupon code copied to clipboard"),
                            ),
                        )
                      }
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Copy discount code"
                    >
                      <Icon name="Copy" className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="text-right">
            <p className="text-muted-foreground text-xs font-medium uppercase">
              Discount
            </p>
            <p
              className={`font-semibold ${
                isDisabled ? "text-muted-foreground" : "text-white"
              }`}
            >
              {promotion.rate_type === 1
                ? `${promotion.rate}%`
                : `$${promotion.rate}`}{" "}
              OFF
            </p>
          </div>
        </div>

        <div className="border-border mt-5 flex items-center justify-between border-t pt-4">
          <div className="flex items-center gap-2">
            <div
              onClick={(event) => event.stopPropagation()}
              className="flex items-center"
            >
              <Switch
                checked={!!apiEnabled}
                onCheckedChange={() => onToggleStatus(promotion.id)}
                disabled={isUpdating}
              />
            </div>
          </div>

          <div
            className="flex items-center gap-2"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={(event) =>
                handleActionClick(event, () => onEdit(promotion))
              }
              className="text-muted-foreground hover:bg-card hover:text-primary rounded-md p-1.5"
              aria-label="Edit promotion"
              title="Edit promotion"
            >
              <Icon name="Edit" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(event) =>
                handleActionClick(event, () => onDelete(promotion.id))
              }
              disabled={isUpdating}
              className="text-muted-foreground hover:bg-card hover:text-destructive rounded-md p-1.5 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Delete promotion"
              title="Delete promotion"
            >
              <Icon name="Trash" className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionCard;
